import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  createDefaultScenario,
  DEFAULT_PLAID_MODE,
  DEFAULT_PROFILES,
  DEFAULT_RATES,
  GROWTH_PRESETS,
  LEGACY_SCENARIO_STORAGE_KEY,
  LEGACY_SCENARIO_STORAGE_KEY_V2,
  LEGACY_SCENARIO_STORAGE_KEY_V1,
  SCENARIO_STORAGE_KEY,
} from './model/seedDefaults';
import { projectGrowth, DEFAULT_GROWTH_MODEL, DEFAULT_GROWTH_PHASES } from './model/growthProjection';

const PRESET_KEY_MIGRATION = {
  conservative: 'bear',
  aggressive: 'bull',
};

export function migrateGrowthPresetKey(presetKey) {
  return PRESET_KEY_MIGRATION[presetKey] || presetKey;
}

function mergeGrowthDefaults(growth) {
  const preset = migrateGrowthPresetKey(growth?.preset);
  return {
    ...DEFAULT_GROWTH_MODEL,
    ...growth,
    preset,
    growthMode: growth?.growthMode || 'profile',
    phases:
      growth?.phases?.length > 0
        ? growth.phases
        : DEFAULT_GROWTH_PHASES.map((p) => ({ ...p })),
  };
}

function finiteAtLeast(value, fallback, minimum = 0) {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? Math.max(minimum, parsed) : fallback;
}

function normalizeBoolean(value, fallback = false) {
  if (value === true || value === 1 || value === 'true') return true;
  if (value === false || value === 0 || value === 'false') return false;
  return fallback;
}

function normalizeRates(rates) {
  const merged = { ...DEFAULT_RATES, ...(rates || {}) };
  return Object.fromEntries(
    Object.entries(DEFAULT_RATES).map(([key, fallback]) => {
      const minimum = key === 'aiPackTurns' ? 1 : 0;
      const maximum = key === 'stripePercent' ? 1 : Infinity;
      const parsed = finiteAtLeast(merged[key], fallback, minimum);
      return [
        key,
        key.startsWith('aiIncludedTurns') || key === 'aiPackTurns'
          ? Math.min(maximum, Math.floor(parsed))
          : Math.min(maximum, parsed),
      ];
    })
  );
}

function normalizePlan(plan, fallback) {
  const source = { ...fallback, ...(plan || {}) };
  const normalizeLimit = (value, fallbackValue) => {
    if (value == null || value === '') return null;
    return Math.floor(finiteAtLeast(value, fallbackValue ?? 0));
  };
  return {
    ...source,
    monthly: finiteAtLeast(source.monthly, fallback.monthly),
    bankLink: normalizeBoolean(source.bankLink, Boolean(fallback.bankLink)),
    entitiesMax: normalizeLimit(source.entitiesMax, fallback.entitiesMax),
    accountsMax: normalizeLimit(source.accountsMax, fallback.accountsMax),
  };
}

function normalizePhase(phase, index) {
  return {
    ...phase,
    throughMonth: Math.min(36, Math.max(1, Math.floor(finiteAtLeast(phase?.throughMonth, index + 1, 1)))),
    monthlyGrowthPct: Math.min(
      1000,
      Math.max(-95, finiteAtLeast(phase?.monthlyGrowthPct, 0, -95))
    ),
    volatilityPct: Math.min(1000, finiteAtLeast(phase?.volatilityPct, 0)),
    label: phase?.label || `Phase ${index + 1}`,
  };
}

function normalizeGrowth(growth) {
  const merged = mergeGrowthDefaults(growth);
  const phases = (merged.phases || DEFAULT_GROWTH_PHASES)
    .map((phase, index) => normalizePhase(phase, index))
    .sort((a, b) => a.throughMonth - b.throughMonth);
  const profileGrowth = Array.isArray(merged.profileGrowth)
    ? merged.profileGrowth.map((row) => ({
        ...row,
        share0: Math.min(1, finiteAtLeast(row.share0, 0)),
        monthlyGrowthPct: Math.min(
          1000,
          Math.max(-95, finiteAtLeast(row.monthlyGrowthPct, 0, -95))
        ),
      }))
    : [];
  return {
    ...merged,
    growthMode: merged.growthMode === 'phased' ? 'phased' : 'profile',
    model: merged.model === 'deterministic' ? 'deterministic' : 'monte_carlo',
    distribution: ['fixed', 'normal', 'lognormal'].includes(merged.distribution)
      ? merged.distribution
      : 'lognormal',
    startPaying: finiteAtLeast(merged.startPaying, 0),
    horizonMonths: Math.min(36, Math.max(1, Math.floor(finiteAtLeast(merged.horizonMonths, 12, 1)))),
    churnPct: Math.min(100, finiteAtLeast(merged.churnPct, 0)),
    mcRuns: Math.min(5000, Math.max(50, Math.floor(finiteAtLeast(merged.mcRuns, 500, 50)))),
    mcSeed: Number.isFinite(Number(merged.mcSeed))
      ? Math.floor(Number(merged.mcSeed))
      : 20260811,
    phases,
    profileGrowth,
  };
}

function migrateProfiles(profiles, preserveCurrentList = false) {
  const incoming = Array.isArray(profiles) ? profiles : null;
  const source = incoming || [];
  const byId = Object.fromEntries(source.map((p) => [p.id, p]));
  const normalizeProfile = (profile, fallback = {}) => {
    const next = {
      ...fallback,
      ...profile,
      plan: ['free', 'basic', 'premium', 'enterprise'].includes(profile?.plan)
        ? profile.plan
        : fallback.plan || 'basic',
      items: Math.floor(finiteAtLeast(profile?.items, fallback.items ?? 0)),
      books: Math.max(1, Math.floor(finiteAtLeast(profile?.books, fallback.books ?? 1, 1))),
      balanceWaves: Math.floor(
        finiteAtLeast(profile?.balanceWaves, fallback.balanceWaves ?? 0)
      ),
      txRefreshCallsMo: Math.floor(
        finiteAtLeast(profile?.txRefreshCallsMo, fallback.txRefreshCallsMo ?? 0)
      ),
      authEnabled: normalizeBoolean(profile?.authEnabled, Boolean(fallback.authEnabled)),
      aiTurnsMo: Math.floor(
        finiteAtLeast(profile?.aiTurnsMo, fallback.aiTurnsMo ?? 0)
      ),
      addOns: { ...(fallback.addOns || {}), ...(profile?.addOns || {}) },
    };
    if (next.addOns.enrichThousandTx) {
      next.addOns.enrichThousandTx = finiteAtLeast(next.addOns.enrichThousandTx, 0);
    }
    return next;
  };
  if (preserveCurrentList && incoming) {
    return incoming
      .filter((profile) => profile?.id)
      .map((profile) => normalizeProfile(profile));
  }

  const builtIns = DEFAULT_PROFILES.map((def) =>
    normalizeProfile(byId[def.id] || def, def)
  );
  const builtInIds = new Set(DEFAULT_PROFILES.map((def) => def.id));
  const custom = source
    .filter((profile) => profile?.id && !builtInIds.has(profile.id))
    .map((profile) => normalizeProfile(profile));
  return [...builtIns, ...custom];
}

export function normalizeEconomicsScenario(parsed) {
  const source = parsed && typeof parsed === 'object' ? parsed : {};
  const defaults = createDefaultScenario();
  const incomingPlans = source.plans && typeof source.plans === 'object' ? source.plans : {};
  const plans = Object.fromEntries(
    Object.entries(defaults.plans).map(([slug, plan]) => [
      slug,
      normalizePlan(incomingPlans[slug], plan),
    ])
  );
  return {
    ...defaults,
    ...source,
    version: 4,
    plaidMode: ['payg', 'growth_platform'].includes(source.plaidMode)
      ? source.plaidMode
      : DEFAULT_PLAID_MODE,
    rates: normalizeRates(source.rates),
    plans,
    profiles: migrateProfiles(source.profiles, Number(source.version) === 4).map((profile) => ({
      ...profile,
      authEnabled: profile.authEnabled && Boolean(plans[profile.plan]?.bankLink),
      aiTurnsMo:
        profile.plan === 'premium' || profile.plan === 'enterprise'
          ? profile.aiTurnsMo
          : 0,
    })),
    growth: normalizeGrowth({
      ...defaults.growth,
      ...(source.growth || {}),
      profileGrowth: Array.isArray(source.growth?.profileGrowth)
        ? source.growth.profileGrowth
        : defaults.growth.profileGrowth,
    }),
  };
}

function loadScenario() {
  try {
    let raw = localStorage.getItem(SCENARIO_STORAGE_KEY);
    let migratedFromLegacy = false;
    let legacyKey = null;

    for (const key of [
      LEGACY_SCENARIO_STORAGE_KEY,
      LEGACY_SCENARIO_STORAGE_KEY_V2,
      LEGACY_SCENARIO_STORAGE_KEY_V1,
    ]) {
      if (raw) break;
      raw = localStorage.getItem(key);
      if (raw) {
        migratedFromLegacy = true;
        legacyKey = key;
      }
    }

    if (!raw) {
      const fresh = createDefaultScenario();
      localStorage.setItem(SCENARIO_STORAGE_KEY, JSON.stringify(fresh));
      return fresh;
    }

    const parsed = JSON.parse(raw);
    if (![1, 2, 3, 4].includes(parsed?.version)) {
      const fresh = createDefaultScenario();
      localStorage.setItem(SCENARIO_STORAGE_KEY, JSON.stringify(fresh));
      return fresh;
    }

    const scenario = normalizeEconomicsScenario(parsed);

    localStorage.setItem(SCENARIO_STORAGE_KEY, JSON.stringify(scenario));
    if (migratedFromLegacy && legacyKey) localStorage.removeItem(legacyKey);

    return scenario;
  } catch {
    const fresh = createDefaultScenario();
    try {
      localStorage.setItem(SCENARIO_STORAGE_KEY, JSON.stringify(fresh));
    } catch {
      /* storage may be unavailable in private/SSR contexts */
    }
    return fresh;
  }
}

function persistScenario(scenario) {
  try {
    localStorage.setItem(SCENARIO_STORAGE_KEY, JSON.stringify(scenario));
  } catch {
    /* Keep the in-memory scenario usable when storage is unavailable/full. */
  }
}

export function useEconomicsScenario() {
  const [scenario, setScenarioState] = useState(loadScenario);

  const setScenario = useCallback((updater) => {
    setScenarioState((prev) => {
      const next = typeof updater === 'function' ? updater(prev) : updater;
      const normalized = normalizeEconomicsScenario(next);
      persistScenario(normalized);
      return normalized;
    });
  }, []);

  useEffect(() => {
    const onStorage = (event) => {
      if (event.key === SCENARIO_STORAGE_KEY && event.newValue) {
        try {
          setScenarioState(normalizeEconomicsScenario(JSON.parse(event.newValue)));
        } catch {
          /* ignore */
        }
      }
    };
    window.addEventListener('storage', onStorage);
    return () => window.removeEventListener('storage', onStorage);
  }, []);

  const projection = useMemo(() => projectGrowth(scenario), [scenario]);

  const resetToDefaults = useCallback(() => {
    const fresh = createDefaultScenario();
    setScenario(fresh);
    return fresh;
  }, [setScenario]);

  const applyGrowthPreset = useCallback(
    (presetKey) => {
      const migratedKey = migrateGrowthPresetKey(presetKey);
      const preset = GROWTH_PRESETS[migratedKey];
      if (!preset) return;
      setScenario((prev) => ({
        ...prev,
        growth: mergeGrowthDefaults({
          preset: migratedKey,
          startPaying: preset.startPaying,
          horizonMonths: preset.horizonMonths,
          churnPct: preset.churnPct,
          profileGrowth: preset.profileGrowth.map((row) => ({ ...row })),
          phases: preset.phases?.map((p) => ({ ...p })),
          model: preset.model,
          distribution: preset.distribution,
          mcRuns: preset.mcRuns,
        }),
      }));
    },
    [setScenario]
  );

  const exportJson = useCallback(() => {
    const blob = new Blob([JSON.stringify(scenario, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const anchor = document.createElement('a');
    anchor.href = url;
    anchor.download = `ffc-economics-scenario-${scenario.asOf || 'export'}.json`;
    anchor.click();
    URL.revokeObjectURL(url);
  }, [scenario]);

  const exportCsv = useCallback(() => {
    const { months, mc } = projection;
    const header = mc
      ? 'month,paying_p50,mrr_p10,mrr_p50,mrr_p90,total_revenue_p10,total_revenue_p50,total_revenue_p90,profit_p10,profit_p50,profit_p90,plaid_cogs_p10,plaid_cogs_p50,plaid_cogs_p90,stripe_fees_p10,stripe_fees_p50,stripe_fees_p90,ai_cogs_p10,ai_cogs_p50,ai_cogs_p90,ai_pack_revenue_p10,ai_pack_revenue_p50,ai_pack_revenue_p90,auth_once_cogs_p10,auth_once_cogs_p50,auth_once_cogs_p90,pre_stripe_margin_pct_p10,pre_stripe_margin_pct_p50,pre_stripe_margin_pct_p90,plaid_mode'
      : 'month,paying,items,mrr,total_revenue,plaid_cogs,auth_once_cogs,stripe_fees,ai_cogs,ai_pack_revenue,profit,pre_stripe_margin_pct,plaid_mode';
    const plaidMode = scenario.plaidMode || DEFAULT_PLAID_MODE;
    const rows = months.map((m) =>
      mc
        ? `${m.label},${m.payingP50},${m.planRevenueP10 ?? m.revenueP10},${m.planRevenueP50 ?? m.revenueP50},${m.planRevenueP90 ?? m.revenueP90},${m.revenueP10},${m.revenueP50},${m.revenueP90},${m.profitP10},${m.profitP50},${m.profitP90},${m.plaidCogsP10 ?? m.plaidCogs ?? 0},${m.plaidCogsP50 ?? m.plaidCogs ?? 0},${m.plaidCogsP90 ?? m.plaidCogs ?? 0},${m.stripeFeesP10 ?? m.stripeFees ?? 0},${m.stripeFeesP50 ?? m.stripeFees ?? 0},${m.stripeFeesP90 ?? m.stripeFees ?? 0},${m.aiCogsP10 ?? m.aiCogs ?? 0},${m.aiCogsP50 ?? m.aiCogs ?? 0},${m.aiCogsP90 ?? m.aiCogs ?? 0},${m.aiPackRevenueP10 ?? m.aiPackRevenue ?? 0},${m.aiPackRevenueP50 ?? m.aiPackRevenue ?? 0},${m.aiPackRevenueP90 ?? m.aiPackRevenue ?? 0},${m.authOnceCogsP10 ?? m.authOnceCogs ?? 0},${m.authOnceCogsP50 ?? m.authOnceCogs ?? 0},${m.authOnceCogsP90 ?? m.authOnceCogs ?? 0},${(((m.marginPctP10 ?? m.marginPct ?? 0) * 100)).toFixed(1)},${(((m.marginPctP50 ?? m.marginPct ?? 0) * 100)).toFixed(1)},${(((m.marginPctP90 ?? m.marginPct ?? 0) * 100)).toFixed(1)},${plaidMode}`
        : `${m.label},${m.paying},${m.items},${m.planRevenue ?? m.revenue},${m.revenue},${m.plaidCogs},${m.authOnceCogs ?? 0},${m.stripeFees},${m.aiCogs ?? 0},${m.aiPackRevenue ?? 0},${m.profit},${((m.marginPct ?? 0) * 100).toFixed(1)},${plaidMode}`
    );
    const blob = new Blob([[header, ...rows].join('\n')], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const anchor = document.createElement('a');
    anchor.href = url;
    anchor.download = `ffc-economics-projection-${scenario.asOf || 'export'}.csv`;
    anchor.click();
    URL.revokeObjectURL(url);
  }, [projection, scenario.asOf, scenario.plaidMode]);

  return {
    scenario,
    setScenario,
    projection,
    resetToDefaults,
    applyGrowthPreset,
    exportJson,
    exportCsv,
  };
}
